import { useState } from 'react';
import {
  createFrontendPlugin,
  PageBlueprint,
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/frontend-plugin-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  InfoCard,
  WarningPanel,
  Header,
  Page,
  Content,
} from '@backstage/core-components';
import {
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Grid,
  Typography,
  Snackbar,
  CircularProgress,
  Link,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import CloudIcon from '@material-ui/icons/Cloud';
import { networkFoundationPermission } from './conduitPermissions';

function NetworkFoundationForm() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [form, setForm] = useState({
    environment_name: '',
    vpc_cidr_block: '',
    tier_subnet_newbits: 8,
    az_count: 2,
    enable_public_tier: true,
    enable_private_tier: true,
    enable_isolated_tier: false,
    nat_strategy: 'single',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ runId: string; runUrl: string; status: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = (field: string, value: string | number | boolean) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('conduit-network-foundation');
      const response = await fetchApi.fetch(`${baseUrl}/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || `Request failed (${response.status})`);
      } else {
        setResult(data);
      }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header title="Network Foundation" subtitle="Create VPC, subnets, and routing infrastructure via Terraform Cloud" />
      <Content>
        <RequirePermission
          permission={networkFoundationPermission}
          errorPage={
            <WarningPanel title="Access Denied" severity="error">
              This function requires the <strong>Platform Engineer</strong> or{' '}
              <strong>Platform Admin</strong> role.
            </WarningPanel>
          }
        >
          <InfoCard title="Network Parameters">
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Environment Name"
                    helperText="Short identifier for the product/environment (e.g., conduit-prod)"
                    value={form.environment_name}
                    onChange={e => set('environment_name', e.target.value)}
                    required
                    fullWidth
                    inputProps={{ pattern: '^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="VPC CIDR Block"
                    helperText="Address space for this VPC (e.g., 10.0.0.0/16)"
                    value={form.vpc_cidr_block}
                    onChange={e => set('vpc_cidr_block', e.target.value)}
                    required
                    fullWidth
                    placeholder="10.0.0.0/16"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Subnet Bits"
                    helperText="Additional bits added to VPC CIDR mask per subnet"
                    type="number"
                    value={form.tier_subnet_newbits}
                    onChange={e => set('tier_subnet_newbits', Number(e.target.value))}
                    fullWidth
                    inputProps={{ min: 1, max: 14 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Availability Zones"
                    helperText="How many AZs to spread subnets across"
                    type="number"
                    value={form.az_count}
                    onChange={e => set('az_count', Number(e.target.value))}
                    fullWidth
                    inputProps={{ min: 1, max: 6 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="NAT Strategy"
                    helperText="Outbound internet for private subnets"
                    select
                    value={form.nat_strategy}
                    onChange={e => set('nat_strategy', e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="single">Single NAT Gateway</MenuItem>
                    <MenuItem value="per_az">One per AZ</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                    Subnet Tiers
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.enable_public_tier}
                        onChange={e => set('enable_public_tier', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Public (load balancers, bastion hosts)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.enable_private_tier}
                        onChange={e => set('enable_private_tier', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Private (application servers, containers)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.enable_isolated_tier}
                        onChange={e => set('enable_isolated_tier', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Isolated (databases, no internet route)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} /> : undefined}
                  >
                    {submitting ? 'Provisioning...' : 'Provision Network'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </InfoCard>
          {result && (
            <Alert severity="success" style={{ marginTop: 16 }}>
              Terraform Cloud run queued.{' '}
              <Link href={result.runUrl} target="_blank" rel="noopener">
                View run {result.runId}
              </Link>{' '}
              — status: {result.status}
            </Alert>
          )}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
          >
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Snackbar>
        </RequirePermission>
      </Content>
    </Page>
  );
}

export default createFrontendPlugin({
  pluginId: 'conduit-network-foundation',
  extensions: [
    PageBlueprint.make({
      params: {
        path: '/network-foundation',
        title: 'Network Foundation',
        icon: <CloudIcon />,
        loader: async () => <NetworkFoundationForm />,
      },
    }),
  ],
});
